
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UserSettings {
  export_format: string;
  show_product_details?: boolean;
  excel_column_config?: any;
  use_content_improver?: boolean;
  use_translation_improver?: boolean;
  collection_sort_field?: string;
  collection_sort_direction?: string;
  translation_sort_field?: string;
  translation_sort_direction?: string;
  [key: string]: any;
}

export const useUserSettings = () => {
  const queryClient = useQueryClient();
  
  const { data: settings, isLoading } = useQuery({
    queryKey: ["userSettings"],
    queryFn: async (): Promise<UserSettings | null> => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return null;

      const { data, error } = await supabase
        .from("user_settings")
        .select("*")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching user settings:", error);
        return null;
      }

      // If no settings exist, return defaults
      if (!data) {
        return { 
          export_format: 'csv',
          show_product_details: false,
          excel_column_config: {
            article_number_columns: [],
            product_name_columns: [],
            description_columns: [],
            details_columns: [],
            additional_info_columns: []
          },
          use_content_improver: true,
          use_translation_improver: true,
          collection_sort_field: 'created_at',
          collection_sort_direction: 'desc',
          translation_sort_field: 'created_at',
          translation_sort_direction: 'desc'
        };
      }

      return data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const updateSetting = async (key: string, value: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        toast.error("You must be logged in to update settings");
        return;
      }

      console.log(`Updating setting ${key} to:`, value);

      // First, check if user settings record exists
      const { data: existingSettings, error: fetchError } = await supabase
        .from("user_settings")
        .select("*")
        .eq("user_id", session.user.id)
        .maybeSingle();

      if (fetchError) {
        console.error("Error fetching existing settings:", fetchError);
        toast.error("Failed to fetch current settings");
        return;
      }

      let result;
      if (existingSettings) {
        // Update existing record
        const { error: updateError } = await supabase
          .from("user_settings")
          .update({
            [key]: value,
            updated_at: new Date().toISOString()
          })
          .eq("user_id", session.user.id);

        if (updateError) {
          console.error("Error updating user setting:", updateError);
          toast.error("Failed to update setting");
          return;
        }
      } else {
        // Insert new record with proper defaults
        const { error: insertError } = await supabase
          .from("user_settings")
          .insert({
            user_id: session.user.id,
            [key]: value,
            export_format: key === 'export_format' ? value : 'csv',
            show_product_details: key === 'show_product_details' ? value : false,
            use_content_improver: key === 'use_content_improver' ? value : true,
            use_translation_improver: key === 'use_translation_improver' ? value : true,
            excel_column_config: key === 'excel_column_config' ? value : {
              article_number_columns: [],
              product_name_columns: [],
              description_columns: [],
              details_columns: [],
              additional_info_columns: []
            },
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          console.error("Error inserting user setting:", insertError);
          toast.error("Failed to create setting");
          return;
        }
      }

      console.log(`Successfully updated setting ${key}`);

      // Invalidate and refetch the settings
      queryClient.invalidateQueries({ queryKey: ["userSettings"] });
      toast.success("Setting updated successfully");
    } catch (error) {
      console.error("Error updating setting:", error);
      toast.error("Failed to update setting");
    }
  };

  return {
    settings: settings || { 
      export_format: 'csv',
      show_product_details: false,
      excel_column_config: {
        article_number_columns: [],
        product_name_columns: [],
        description_columns: [],
        details_columns: [],
        additional_info_columns: []
      },
      use_content_improver: true,
      use_translation_improver: true,
      collection_sort_field: 'created_at',
      collection_sort_direction: 'desc',
      translation_sort_field: 'created_at',
      translation_sort_direction: 'desc'
    },
    isLoading,
    updateSetting
  };
};
